import axiosInstance from '@/app/api/axiosInstance';

/**
 * Thin client over the shared /attachments endpoints. Used for lead shop photos
 * (`entity_type: 'lead'`), which is what replaces posting pictures into a WhatsApp
 * group.
 */

export interface Attachment {
    id: number;
    entity_type: string;
    entity_id: number;
    file_name: string;
    file_size: number;
    mime_type: string;
    created_at?: string | null;
}

/** R2 rejects anything larger; a raw phone photo will usually exceed it. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 1600;

/**
 * Downscale a camera photo before upload.
 *
 * A modern phone camera produces 4-12MB JPEGs and the org's R2 config caps uploads
 * at 5MB, so uploading the raw file fails in the field — exactly where retrying is
 * hardest. Falls back to the original file if canvas encoding fails for any reason.
 */
export const compressImage = async (file: File): Promise<File> => {
    if (!file.type.startsWith('image/')) return file;

    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));

        // Small enough already and under the cap — leave it alone.
        if (scale === 1 && file.size <= MAX_UPLOAD_BYTES) return file;

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);

        const ctx = canvas.getContext('2d');
        if (!ctx) return file;
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

        const blob = await new Promise<Blob | null>((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', 0.82)
        );
        if (!blob) return file;

        const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
        return new File([blob], name, { type: 'image/jpeg' });
    } catch {
        return file;
    }
};

export interface UploadResult {
    attachment_id: number;
    file_name: string;
    file_size: number;
    mime_type: string;
    upload_status: string;
}

export const uploadLeadPhoto = async (
    leadId: number,
    file: File,
    siteId?: number | null
): Promise<UploadResult> => {
    const prepared = await compressImage(file);

    const form = new FormData();
    form.append('file', prepared);
    form.append('entity_type', 'lead');
    form.append('entity_id', String(leadId));
    if (siteId) form.append('site_id', String(siteId));

    const response = await axiosInstance.post<UploadResult>(
        '/attachments/upload',
        form,
        // Let the browser set the multipart boundary; the shared instance defaults
        // to application/json.
        { headers: { 'Content-Type': undefined as unknown as string } }
    );
    return response.data;
};

export const getLeadPhotos = async (leadId: number): Promise<Attachment[]> => {
    const response = await axiosInstance.get<Attachment[]>(`/attachments/entity/lead/${leadId}`);
    return response.data ?? [];
};

export const getAttachmentViewUrl = async (attachmentId: number): Promise<string> => {
    const response = await axiosInstance.get<{ url: string }>(
        `/attachments/${attachmentId}/view-url`
    );
    return response.data.url;
};

export const deleteAttachment = async (attachmentId: number): Promise<void> => {
    await axiosInstance.delete(`/attachments/${attachmentId}`);
};
